import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import qs from 'query-string'
import classNames from 'classnames'
import { useLocation } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/react-hooks'

import WATCHLISTS_FOR_CONTENT_QUERY from '../../../../graphql/queries/watchlists-for-content.gql'
import CREATE_NAMED_LIST_MUTATION from '../../../../graphql/mutations/create-named-list.gql'
import UPDATE_WATCHLISTS_FOR_CONTENT_MUTATION from '../../../../graphql/mutations/update-watchlists-for-content.gql'
import CONTENT_ITEM_QUERY from '../../../../graphql/queries/content-item.gql'
import SCREEN_QUERY from '../../../../graphql/queries/screen.gql'
import { useProfile } from '../../../hooks/use-profile'
import {
  episodeHouseId,
  getShowTitleFromContentItem,
  myListGa,
  seasonHouseId,
  seriesTitleHouseId
} from '../../../lib/analytics/ga'
import {
  customDimensions,
  getCategoryNameFromContentItem
} from '../../../lib/analytics/custom-dimensions'
import {
  dataLayerAddToMyList,
  getAdditionalGenreFromContentItem
} from '../../../lib/analytics/datalayer'
import { getGQLErrorMsg } from '../../../lib/apollo'
import { STATIC_SCREENS } from '../../../constants'
import { segmentTrackAddMyList } from '../../../segment/segment-track'
import { buildWatchlistUpdateInput } from './utils/build-watchlist-update-input'
import {
  defaultAnalyticsComponentName,
  labels,
  maxWatchlists,
  modalScreens,
  watchlistTypes
} from '../constants'
import neonLoadingImg from '../../../../images/neon/loading.svg'
import ModalHeader from './components/modal-header'
import AddToListScreen from './screens/add-to-list-screen'
import CreateListScreen from './screens/create-list-screen'
import styles from './modal-add-content-to-lists.css'

const getComponentName = renderedInComponent => renderedInComponent || defaultAnalyticsComponentName

const insertAfterDefaultList = (watchlists, newWatchlist) => {
  const defaultListIndex = watchlists.findIndex(watchlist => watchlist.listType === watchlistTypes.defaultList)
  const insertIndex = defaultListIndex === -1 ? 0 : defaultListIndex + 1

  return [
    ...watchlists.slice(0, insertIndex),
    newWatchlist,
    ...watchlists.slice(insertIndex)
  ]
}

const trackAddToMyList = ({
  contentItem,
  currentProfile,
  renderedInComponent
}) => {
  myListGa({
    action: 'Add',
    label: contentItem.title,
    [customDimensions.ShowTitle]: getShowTitleFromContentItem(contentItem),
    [customDimensions.CategoryName]: getCategoryNameFromContentItem(contentItem),
    [customDimensions.ProfileId]: currentProfile?.id,
    [customDimensions.AdditionalGenre]: getAdditionalGenreFromContentItem(contentItem),
    [customDimensions.SeriesTitleHouseId]: seriesTitleHouseId(contentItem),
    [customDimensions.SeasonHouseId]: seasonHouseId(contentItem),
    [customDimensions.EpisodeHouseId]: episodeHouseId(contentItem),
    [customDimensions.ComponentName]: getComponentName(renderedInComponent)
  })
  dataLayerAddToMyList(contentItem)
  segmentTrackAddMyList(contentItem)
}

const trackRemoveFromMyList = ({
  contentItem,
  currentProfile,
  renderedInComponent
}) => {
  myListGa({
    action: 'Remove',
    label: contentItem.title,
    [customDimensions.ShowTitle]: contentItem.title,
    [customDimensions.CategoryName]: getCategoryNameFromContentItem(contentItem),
    [customDimensions.ProfileId]: currentProfile?.id,
    [customDimensions.AdditionalGenre]: getAdditionalGenreFromContentItem(contentItem),
    [customDimensions.SeriesTitleHouseId]: seriesTitleHouseId(contentItem),
    [customDimensions.SeasonHouseId]: seasonHouseId(contentItem),
    [customDimensions.EpisodeHouseId]: episodeHouseId(contentItem),
    [customDimensions.ComponentName]: getComponentName(renderedInComponent)
  })
}

const getRefetchQueries = (contentItemId, location) => {
  // Refresh the per-content watchlist state used by AddOrRemoveFromListButton
  const refetchQueries = [
    {
      query: WATCHLISTS_FOR_CONTENT_QUERY,
      variables: { contentItemId }
    }
  ]

  // When the modal is opened from /my-lists, refresh the ScreenQuery
  // so removed/added tiles are reflected on the My Lists screen
  if (location.pathname === STATIC_SCREENS.MY_LISTS.screenId) {
    refetchQueries.push({
      query: SCREEN_QUERY,
      variables: {
        screenId: STATIC_SCREENS.MY_LISTS.screenId
      }
    })
  }

  return refetchQueries
}

const AddContentToListsModal = ({ onRequestClose }) => {
  const location = useLocation()
  const { contentItemId, renderedInComponent } = qs.parse(location.search)
  const stateContentItem = location.state?.contentItem
  const { currentProfile } = useProfile('cache-only')
  const isKidProfile = Boolean(currentProfile?.isKid)
  const [screen, setScreen] = useState(modalScreens.addToList)
  const [watchlists, setWatchlists] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [error, setError] = useState('')

  const {
    data,
    loading,
    error: queryError
  } = useQuery(WATCHLISTS_FOR_CONTENT_QUERY, {
    variables: { contentItemId },
    skip: !contentItemId,
    fetchPolicy: 'network-only'
  })

  const { data: contentItemData } = useQuery(CONTENT_ITEM_QUERY, {
    variables: { id: contentItemId },
    skip: !contentItemId || Boolean(stateContentItem),
    fetchPolicy: 'cache-first'
  })

  const [createNamedList, { loading: creating }] = useMutation(CREATE_NAMED_LIST_MUTATION)
  const [updateWatchlistsForContent, { loading: saving }] = useMutation(
    UPDATE_WATCHLISTS_FOR_CONTENT_MUTATION,
    {
      refetchQueries: getRefetchQueries(contentItemId, location),
      awaitRefetchQueries: true
    }
  )

  useEffect(() => {
    const nextWatchlists = data?.watchlistsForContent ?? []
    const displayWatchlists = isKidProfile
      ? nextWatchlists.filter(watchlist => watchlist.listType === watchlistTypes.defaultList)
      : nextWatchlists

    setWatchlists(displayWatchlists)
    setSelectedIds(displayWatchlists.filter(watchlist => watchlist.hasContent).map(watchlist => watchlist.id))
  }, [data, isKidProfile])

  const toggleWatchlist = (id) => {
    setSelectedIds(prevSelectedIds => (
      prevSelectedIds.includes(id)
        ? prevSelectedIds.filter(selectedId => selectedId !== id)
        : [...prevSelectedIds, id]
    ))
  }

  const handleCreateList = (listName) => {
    setError('')
    createNamedList({ variables: { name: listName.trim() } })
      .then(({ data: createData }) => {
        const createdList = createData?.createNamedList
        if (!createdList) return

        const newWatchlist = {
          id: createdList.id,
          name: createdList.name,
          listType: watchlistTypes.namedList,
          hasContent: false
        }

        setWatchlists(prevWatchlists => insertAfterDefaultList(prevWatchlists, newWatchlist))
        setSelectedIds(prevSelectedIds => [...prevSelectedIds, newWatchlist.id])
        setScreen(modalScreens.addToList)
      })
      .catch(createError => {
        setError(getGQLErrorMsg(createError))
      })
  }

  const handleDone = () => {
    const input = buildWatchlistUpdateInput(contentItemId, watchlists, selectedIds)
    const contentItem = stateContentItem || contentItemData?.contentItem

    setError('')
    updateWatchlistsForContent({ variables: { input } })
      .then(() => {
        if (input.addTo.length && contentItem) {
          trackAddToMyList({
            contentItem,
            currentProfile,
            renderedInComponent
          })
        }

        if (input.removeFrom.length && contentItem) {
          trackRemoveFromMyList({
            contentItem,
            currentProfile,
            renderedInComponent
          })
        }

        onRequestClose()
      })
      .catch((updateError) => {
        setError(getGQLErrorMsg(updateError))
      })
  }

  const canCreateList = !isKidProfile && watchlists.length < maxWatchlists
  const isCreateScreen = screen === modalScreens.createList

  if (loading || Boolean(queryError || !contentItemId)) {
    return (
      <div className={classNames(styles.container, styles.loadingContainer)}>
        <img src={neonLoadingImg} alt="" />
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <ModalHeader
        title={isCreateScreen ? labels.createListTitle : labels.addToListTitle}
        showBackButton={isCreateScreen}
        onBack={() => {
          setError('')
          setScreen(modalScreens.addToList)
        }}
      />

      {isCreateScreen ? (
        <CreateListScreen
          creating={creating}
          onCreateList={handleCreateList}
        />
      ) : (
        <AddToListScreen
          canCreateList={canCreateList}
          watchlists={watchlists}
          selectedIds={selectedIds}
          saving={saving}
          onCreateList={() => {
            setError('')
            setScreen(modalScreens.createList)
          }}
          onToggleWatchlist={toggleWatchlist}
          onDone={handleDone}
        />
      )}
      {error && <div className={styles.error}>{error}</div>}
    </div>
  )
}

AddContentToListsModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired
}

export default AddContentToListsModal
